import {
  wearsSteampack,
  CHARACTER,
  ITEMS,
  T,
  count,
  fuelToStoke,
  furnaceCapacity,
  inGravityField,
  holdingOn,
  atHelmOrTurret,
  isClimbable,
  isHelm,
  isMachine,
  isPartOf,
  isTurretSeat,
  playerWorldCenter,
  tileAt,
  tileNear,
  waterAt,
  type Inventory,
  type PlayerState,
  type ShipState,
  type Sim,
} from "@dyson/shared";
import type { CharacterMode } from "../render/CharacterMesh";

/** The status line's keys and clicks, as the touch controls' buttons and taps (see TouchControls). In order: longer phrases first. */
const TOUCH_WORDS: [RegExp, string][] = [
  [/W\/S up\/down · A\/D back\/forward · Q\/E turn/, "stick lifts, sinks, backs and goes forward · ↺ ↻ turn"],
  [/leave the helm \(right click\)/g, "leave the helm (Leave helm)"],
  [/right click to climb out/g, "Climb out to leave"],
  [/right click to leave/g, "Leave helm to leave"],
  [/Right click the (helm|turret|harpoon) to /g, "Tap the $1 (placing) to "],
  [/right click the furnace to /g, "tap the furnace (placing) to "],
  [/hold W in the air/g, "hold Jump in the air"],
  [/hold W to reel in/g, "hold Jump to reel in"],
  [/WASD crawls along it/g, "the stick crawls along it"],
  [/right click the flames/g, "tap the flames (placing)"],
  [/Right click to /g, "Tap (placing) to "],
  [/\(right click\)/g, "(tap, placing)"],
  [/aim with the mouse · click to fire/g, "drag to aim · Fire fires"],
  [/aim with the mouse · click fires/g, "drag to aim · Fire fires"],
  [/click and hold to reel in/g, "hold Fire to reel in"],
  [/click fires the bow guns/g, "Fire fires the bow guns"],
  [/right click or jump to let go/g, "a placing tap or Jump to let go"],
  [/\bG lets go/g, "Grab lets go"],
  [/lets go \(G\)/g, "lets go (Grab)"],
  [/right click throws/g, "a placing tap throws"],
  [/WASD moves you/g, "the stick moves you"],
  [/WASD near a rope/g, "the stick near a rope"],
  [/WASD flies you/g, "the stick flies you"],
  [/WASD to swim/g, "the stick swims"],
  [/Q\/E spin/g, "turning spins you"],
];

/** `text` with its keys and clicks named as the touch controls have them. */
export function touchWording(text: string): string {
  for (const [re, to] of TOUCH_WORDS) text = text.replace(re, to);
  return text;
}

/**
 * The status line under the screen: what you're doing and the keys for it.
 * `flown` is the predicted state of the ship you're piloting, if you are.
 */
export function statusText(me: PlayerState | undefined, sim: Sim | undefined, flown: ShipState | undefined, inventory: Inventory): string {
  if (!me || !sim) return "";
  return statusLine(me, sim, flown, inventory);
}

function statusLine(me: PlayerState, sim: Sim, flown: ShipState | undefined, inventory: Inventory): string {
  if (me.gunning !== null) {
    const ship = me.frame ? sim.ships.get(me.frame) : undefined;
    if (!ship || !isMachine(ship.grid.tiles[me.gunning]!, "harpoon")) return "In the turret · aim with the mouse · click to fire · right click to climb out";
    const t = ship.state.tether;
    if (!t) return "At the harpoon · aim with the mouse · click fires a line along the barrel: into rock, a whale, a gas bag or a ship · right click to climb out";
    if (t.anchor !== me.gunning) return "Another harpoon's line is out · right click to climb out";
    const what = t.creature ? "Harpooned something alive!" : t.ship ? "Hooked a ship: towing it" : "The line's fast in rock";
    return `${what} · click and hold to reel in · G lets go · right click to climb out`;
  }
  if (me.piloting) {
    const body = flown && sim.ships.get(flown.id);
    if (body && !body.stats.furnaces) return "No furnace aboard: build one to make steam · right click to leave";
    if (flown && flown.fuel <= 0) return "Out of fuel! · leave the helm (right click) and feed the furnace coal, starbark or wood";
    if (flown && flown.water <= 0) return "Out of water! · leave the helm (right click) and feed the furnace water";
    const guns = body?.stats.bowGuns.length ? " · click fires the bow guns" : "";
    const anchor = flown?.tether ? " · on a harpoon line: whoever mans it lets go (G)" : "";
    return `At the helm · W/S up/down · A/D back/forward · Q/E turn${guns}${anchor} · right click to leave`;
  }
  if (me.grapple) return "Hooked! · hold W to reel in · right click or jump to let go";
  // Standing by a furnace with room for more: say what it has and how to feed it.
  const c0 = playerWorldCenter(me, sim);
  const furnace = tileNear(sim, c0, "furnace", CHARACTER.reach)?.ship;
  if (furnace) {
    const cap = furnaceCapacity(furnace);
    const pct = (n: number) => Math.round((n / cap) * 100);
    const levels = `Furnace: fuel ${pct(furnace.state.fuel)}% · water ${pct(furnace.state.water)}%`;
    const fuel = furnace.state.fuel < cap ? fuelToStoke(inventory) : undefined;
    const water = furnace.state.water < cap && count(inventory, "water") > 0;
    if (fuel || water) return `${levels} · right click the furnace to feed ${[fuel && ITEMS[fuel].name.toLowerCase(), water && "water"].filter(Boolean).join(" and ")}`;
    if (furnace.state.fuel < cap || furnace.state.water < cap) return `${levels} · you need coal, starbark or wood, and water (dig it up), to feed it`;
  }
  const ship = me.frame ? sim.ships.get(me.frame) : undefined;
  if (ship) {
    const t = tileAt(ship.grid, Math.floor(me.position.x), Math.floor(me.position.y + CHARACTER.height / 2));
    if (isHelm(t)) return ship.state.pilotId && ship.state.pilotId !== me.id ? "Someone else is at the helm" : "Right click the helm to take it";
    // Head up in a turret's seat (climbing the ladder into it).
    const head = tileAt(ship.grid, Math.floor(me.position.x), Math.floor(me.position.y + CHARACTER.height - 0.4));
    if (isTurretSeat(head) || isTurretSeat(t)) return isMachine(head, "harpoon") || isMachine(t, "harpoon") ? "Right click the harpoon to man it" : "Right click the turret to man it";
    if (ship.state.fires) return "Fire aboard! · right click the flames holding water to put them out";
    const aboard = ship.stats.helms ? "Aboard a ship" : "Aboard drifting wreckage";
    if (holdingOn(me, sim)) return aboard + " · zero-g: WASD crawls along the floor, walls and ceiling · push away to kick off";
    return aboard + " · zero-g: floating free, WASD paddles you slowly · Q/E spin";
  }
  // A turret built on an asteroid.
  if (atHelmOrTurret(me, sim)) return "Right click the turret to man it";
  const c = c0;
  if (waterAt(sim, c)) return "Swimming · WASD to swim";
  const pack = wearsSteampack(me.gear);
  if (me.launched) return pack ? "Launched! · once clear of the rock, WASD flies you on the steampack · right click throws the grapple back" : "Launched! · right click throws the grapple back at the rock before you drift off";
  const grapple = me.holding === "grapple" ? " · right click throws the grapple at rock or a ship" : "";
  if (inGravityField(sim, c)) return "On an asteroid · double jump to launch" + grapple + (pack ? " · hold W in the air to fly on the steampack" : " · make a steampack to fly");
  if (holdingOn(me, sim)) return "Holding onto rock · WASD crawls along it · push away from it to kick off" + grapple;
  if (pack) return "In open space · WASD flies you on the steampack (no steam used) · Q/E spin you round" + (me.holding === "grapple" ? " · right click throws the grapple at rock or a ship" : " · R starts you over on the stream");
  return "Adrift in open space · no steampack: the air slows you" + (me.holding === "grapple" ? " · right click throws the grapple at rock or a ship within reach" : " · hold the grapple and right click throws it") + " · R starts you over on the stream";
}

/** Which animation a player gets: at the helm, on a ladder, swimming, drifting in zero-g, or walking and jumping. */
export function characterMode(p: PlayerState, sim: Sim): CharacterMode {
  if (p.piloting || p.gunning !== null) return "helm";
  if (p.vine) return "climb";
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  const c = playerWorldCenter(p, sim);
  let tile: number = T.EMPTY;
  if (ship) tile = tileAt(ship.grid, Math.floor(p.position.x), Math.floor(p.position.y + CHARACTER.height / 2));
  else for (const a of sim.asteroids) tile ||= tileAt(a.grid, Math.floor(c.x - a.def.ox), Math.floor(c.y - a.def.oy));
  if (tile === T.WATER) return "swim";
  if (p.grounded) return "ground";
  if (isClimbable(tile)) return "climb";
  // Zero-g aboard a ship as in open space.
  if (ship || !inGravityField(sim, c)) return "drift";
  return "air";
}
